import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { GeoJSONSource, Map as MapLibreMap, MapGeoJSONFeature, MapLayerMouseEvent } from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { DataRow } from "../types";
import { branchByNumber, branchForDma } from "../data/branches";
import { jobsGeoJson } from "../lib/mapJobs";

type Properties = Record<string, unknown>;
type FeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Geometry, Properties>;
type LayerKey = "dma" | "mainPipe" | "distributionPipe" | "rtu" | "uz" | "tunnel" | "hydrology" | "chaopaya";
type LayerSetting = { visible: boolean; labels: boolean; labelField: string };
type Selection = { layer: string; geometry: GeoJSON.Geometry; properties: Properties };

type ManifestLayer = {
  name: string;
  url?: string;
  urlTemplate?: string;
  indexUrl?: string;
  geometry: "point" | "line" | "polygon";
  count: number;
  minZoom?: number;
  labelFields?: string[];
  dmas?: { code: string; count: number }[];
  pipeSizes?: number[];
};

type MapManifest = {
  schemaVersion: number;
  datasetVersion: string;
  generatedAt: string;
  sourceCrs: string;
  displayCrs: string;
  bounds: [number, number, number, number];
  layers: Record<LayerKey, ManifestLayer>;
  auditUrl: string;
};

type HydrologyIndex = {
  chunks: { key: string; url: string; count: number; bounds: [number, number, number, number] }[];
};

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const LAYER_ORDER: LayerKey[] = ["mainPipe", "distributionPipe", "dma", "rtu", "uz", "tunnel", "hydrology", "chaopaya"];
const LAYER_LABELS: Record<LayerKey, string> = {
  dma: "ขอบเขต DMA",
  mainPipe: "Main Pipe",
  distributionPipe: "Distribution Pipe",
  rtu: "RTU — จุดรับน้ำเข้า DMA",
  uz: "UZ — จุดวัดบนท่อประธาน",
  tunnel: "Tunnel",
  hydrology: "Hydrology",
  chaopaya: "แม่น้ำเจ้าพระยา",
};
const MAP_LAYER_IDS: Record<LayerKey, string[]> = {
  dma: ["dma-fill", "dma-line"],
  mainPipe: ["main-pipe"],
  distributionPipe: ["distribution-pipe"],
  rtu: ["rtu-points"],
  uz: ["uz-points"],
  tunnel: ["tunnel-line"],
  hydrology: ["hydrology-line"],
  chaopaya: ["chaopaya-fill", "chaopaya-line"],
};
const LABEL_LAYER_IDS: Partial<Record<LayerKey, string>> = {
  dma: "dma-label",
  mainPipe: "main-pipe-label",
  distributionPipe: "distribution-pipe-label",
  rtu: "rtu-label",
  uz: "uz-label",
  tunnel: "tunnel-label",
  hydrology: "hydrology-label",
};
const SOURCE_IDS: Record<LayerKey, string> = {
  dma: "dma",
  mainPipe: "main-pipe-source",
  distributionPipe: "distribution-pipe-source",
  rtu: "rtu-source",
  uz: "uz-source",
  tunnel: "tunnel-source",
  hydrology: "hydrology-source",
  chaopaya: "chaopaya-source",
};

const DETAIL_FIELDS: Record<string, string[]> = {
  dma: ["BLOCKNAME", "BRANCH", "BRANCH_SHORT_NAME", "BRANCH_FULL_NAME", "ZONE", "BLOCK"],
  "main-pipe": ["ID", "TAG", "NAME", "PIPE_SIZE", "MATL", "INST_YEAR", "ENABLED", "DATA_QUALI"],
  "distribution-pipe": ["FID_Distri", "TAG", "NAME", "PIPE_SIZE", "MATL", "INST_YEAR", "DMA_SOURCE", "DMA_DISPLAY"],
  rtu: ["ID", "TAG", "NAME", "PIPE_SIZE", "DMA_SOURCE", "DMA_DISPLAY", "DMA_MATCH_STATUS", "MAIN_PIPE_ID", "MATCH_DISTANCE", "MATCH_STATUS"],
  uz: ["ID", "TAG", "NAME", "PIPE_SIZE", "MAIN_PIPE_ID", "MATCH_DISTANCE", "MATCH_STATUS", "CSTATUS", "NSTATUS"],
  tunnel: ["ID", "TAG", "NAME", "PIPE_SIZE", "MATL", "INST_YEAR", "ENABLED"],
  hydrology: ["SUBTYPECOD", "SUBTYPEC_1", "LBSTATUS", "DATECREATE", "DATEMODIFI"],
  chaopaya: ["CAOPAYA_ID", "AREA", "PERIMETER"],
  jobs: ["jobId", "dmaCode", "branchNumber", "branchShortName", "branchFullName", "status", "reportedDetail", "workDetail"],
};

const FIELD_LABELS: Record<string, string> = {
  ID: "รหัส",
  TAG: "Tag",
  NAME: "ชื่อ",
  PIPE_SIZE: "ขนาดท่อ (มม.)",
  MATL: "วัสดุ",
  INST_YEAR: "ปีติดตั้ง",
  ENABLED: "สถานะใช้งาน",
  DATA_QUALI: "คุณภาพข้อมูล",
  BLOCKNAME: "รหัส DMA",
  BRANCH: "สำนักงานสาขา",
  BRANCH_SHORT_NAME: "ชื่อย่อสาขา",
  BRANCH_FULL_NAME: "ชื่อเต็มสาขา",
  ZONE: "Zone",
  BLOCK: "Block",
  FID_Distri: "รหัสเส้นท่อ",
  DMA_SOURCE: "DMA จากข้อมูลต้นฉบับ",
  DMA_DISPLAY: "DMA ที่ใช้แสดงผล",
  DMA_MATCH_STATUS: "ผลตรวจ DMA",
  MAIN_PIPE_ID: "ท่อประธานที่สัมพันธ์",
  MATCH_DISTANCE: "ระยะจากท่อประธาน (ม.)",
  MATCH_STATUS: "ผลจับคู่ท่อประธาน",
  CSTATUS: "สถานะการสื่อสาร",
  NSTATUS: "สถานะเครือข่าย",
  SUBTYPECOD: "ประเภทร่องน้ำ",
  SUBTYPEC_1: "รายละเอียดประเภท",
  LBSTATUS: "สถานะ",
  DATECREATE: "วันที่สร้าง",
  DATEMODIFI: "วันที่แก้ไข",
  CAOPAYA_ID: "รหัสพื้นที่",
  AREA: "พื้นที่",
  PERIMETER: "เส้นรอบรูป",
  jobId: "หมายเลขงาน",
  dmaCode: "DMA",
  branchNumber: "เลขสาขา",
  branchShortName: "ชื่อย่อสาขา",
  branchFullName: "ชื่อเต็มสาขา",
  status: "สถานะงาน",
  reportedDetail: "รายละเอียดที่รับแจ้ง",
  workDetail: "รายละเอียดการดำเนินงาน",
};

const INITIAL_SETTINGS: Record<LayerKey, LayerSetting> = {
  mainPipe: { visible: true, labels: false, labelField: "PIPE_SIZE" },
  distributionPipe: { visible: false, labels: false, labelField: "PIPE_SIZE" },
  dma: { visible: false, labels: false, labelField: "BLOCKNAME" },
  rtu: { visible: false, labels: false, labelField: "NAME" },
  uz: { visible: false, labels: false, labelField: "NAME" },
  tunnel: { visible: false, labels: false, labelField: "PIPE_SIZE" },
  hydrology: { visible: false, labels: false, labelField: "SUBTYPECOD" },
  chaopaya: { visible: false, labels: false, labelField: "CAOPAYA_ID" },
};

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, credentials: "same-origin", cache: "no-store" });
  if (!response.ok) throw new Error(`โหลด ${url} ไม่สำเร็จ (${response.status})`);
  return response.json() as Promise<T>;
}

async function loadManifest(signal: AbortSignal): Promise<MapManifest> {
  try {
    return await fetchJson<MapManifest>("/api/map/manifest", signal);
  } catch (error) {
    if (signal.aborted) throw error;
    return fetchJson<MapManifest>("/map-data/v1/manifest.json", signal);
  }
}

function fallbackStyle() {
  return {
    version: 8 as const,
    glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
    sources: {
      osm: {
        type: "raster" as const,
        tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "© OpenStreetMap contributors",
      },
    },
    layers: [{ id: "osm", type: "raster" as const, source: "osm", minzoom: 0, maxzoom: 19 }],
  };
}

function featureBounds(feature: GeoJSON.Feature): [[number, number], [number, number]] | null {
  if (!feature.geometry) return null;
  const coordinates: number[][] = [];
  const collect = (value: unknown) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === "number" && typeof value[1] === "number") coordinates.push(value as number[]);
    else value.forEach(collect);
  };
  if (feature.geometry.type === "GeometryCollection") {
    feature.geometry.geometries.forEach((geometry) => collect("coordinates" in geometry ? geometry.coordinates : []));
  } else {
    collect(feature.geometry.coordinates);
  }
  if (!coordinates.length) return null;
  return [
    [Math.min(...coordinates.map((point) => point[0])), Math.min(...coordinates.map((point) => point[1]))],
    [Math.max(...coordinates.map((point) => point[0])), Math.max(...coordinates.map((point) => point[1]))],
  ];
}

function collectionBounds(collection: FeatureCollection): [[number, number], [number, number]] | null {
  const bounds = collection.features.map(featureBounds).filter((item): item is [[number, number], [number, number]] => Boolean(item));
  if (!bounds.length) return null;
  return [
    [Math.min(...bounds.map((item) => item[0][0])), Math.min(...bounds.map((item) => item[0][1]))],
    [Math.max(...bounds.map((item) => item[1][0])), Math.max(...bounds.map((item) => item[1][1]))],
  ];
}

function pointSegmentDistanceMeters(point: [number, number], start: number[], end: number[]): number {
  const latitude = point[1] * Math.PI / 180;
  const scaleX = 111_320 * Math.cos(latitude);
  const scaleY = 110_540;
  const ax = (start[0] - point[0]) * scaleX;
  const ay = (start[1] - point[1]) * scaleY;
  const bx = (end[0] - point[0]) * scaleX;
  const by = (end[1] - point[1]) * scaleY;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const amount = lengthSquared ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSquared)) : 0;
  return Math.hypot(ax + amount * dx, ay + amount * dy);
}

function nearestPipe(point: [number, number], collections: { layer: string; data?: FeatureCollection }[]): { layer: string; properties: Properties; distance: number } | null {
  let nearest: { layer: string; properties: Properties; distance: number } | null = null;
  for (const collection of collections) for (const feature of collection.data?.features ?? []) {
    if (!feature.geometry || (feature.geometry.type !== "LineString" && feature.geometry.type !== "MultiLineString")) continue;
    const lines = feature.geometry.type === "LineString" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
    for (const line of lines) for (let index = 1; index < line.length; index += 1) {
      const distance = pointSegmentDistanceMeters(point, line[index - 1], line[index]);
      if (!nearest || distance < nearest.distance) nearest = { layer: collection.layer, properties: feature.properties ?? {}, distance };
    }
  }
  return nearest;
}

function asSelection(feature: MapGeoJSONFeature): Selection {
  const normalizedLayer = feature.layer.id === "unclustered"
    ? "jobs"
    : feature.layer.id.replace(/-(fill|line|points|label)$/, "").replace("-source", "");
  const properties = feature.properties as Properties;
  if (normalizedLayer === "dma") {
    const branch = branchByNumber(String(properties.BRANCH ?? ""));
    properties.BRANCH_SHORT_NAME = branch?.shortName ?? null;
    properties.BRANCH_FULL_NAME = branch?.fullName ?? null;
  }
  return {
    layer: normalizedLayer,
    geometry: feature.geometry as GeoJSON.Geometry,
    properties,
  };
}

function source(map: MapLibreMap, id: string): GeoJSONSource | undefined {
  return map.getSource(id) as GeoJSONSource | undefined;
}

function setVisibility(map: MapLibreMap, ids: string[], visible: boolean) {
  for (const id of ids) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
}

export default function NetworkMap({ rows, selectedDma, onSelectDma, highlightedDmaCodes = [], fitRequest = 0, onCancelDmaMatch }: { rows: DataRow[]; selectedDma: string | null; onSelectDma: (dma: string | null) => void; highlightedDmaCodes?: string[]; fitRequest?: number; onCancelDmaMatch?: (rowId: string) => Promise<void> }) {
  const shellRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const layerPanelRef = useRef<HTMLElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const manifestRef = useRef<MapManifest | null>(null);
  const dmaDataRef = useRef<FeatureCollection | null>(null);
  const loadedLayersRef = useRef(new Map<LayerKey, FeatureCollection>());
  const hydrologyIndexRef = useRef<HydrologyIndex | null>(null);
  const hydrologyCacheRef = useRef(new Map<string, FeatureCollection>());
  const [manifest, setManifest] = useState<MapManifest | null>(null);
  const [settings, setSettings] = useState(() => ({
    ...INITIAL_SETTINGS,
    dma: { ...INITIAL_SETTINGS.dma, visible: highlightedDmaCodes.length > 0 },
  }));
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>("กำลังโหลดข้อมูลแผนที่…");
  const [selections, setSelections] = useState<Selection[]>([]);
  const [selectionIndex, setSelectionIndex] = useState(0);
  const [cancellingMatch, setCancellingMatch] = useState(false);
  const [layerPanelOpen, setLayerPanelOpen] = useState(() => {
    try { return localStorage.getItem("dma-map-layer-panel-open") !== "false"; }
    catch { return true; }
  });
  const [layerPanelWidth, setLayerPanelWidth] = useState(() => {
    try { return Math.min(440, Math.max(250, Number(localStorage.getItem("dma-map-layer-panel-width")) || 280)); }
    catch { return 280; }
  });
  const selected = selections[selectionIndex] ?? null;
  const jobData = useMemo(() => jobsGeoJson(rows), [rows]);

  useEffect(() => {
    try { localStorage.setItem("dma-map-layer-panel-open", String(layerPanelOpen)); }
    catch { /* Browser storage may be disabled. */ }
  }, [layerPanelOpen]);

  useEffect(() => {
    const panel = layerPanelRef.current;
    if (!panel || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (width >= 250 && width <= 460) {
        setLayerPanelWidth(width);
        try { localStorage.setItem("dma-map-layer-panel-width", String(width)); }
        catch { /* Browser storage may be disabled. */ }
      }
    });
    observer.observe(panel);
    return () => observer.disconnect();
  }, [layerPanelOpen]);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const nextManifest = await loadManifest(controller.signal);
        const [dma, mainPipe] = await Promise.all([
          fetchJson<FeatureCollection>(nextManifest.layers.dma.url!, controller.signal),
          fetchJson<FeatureCollection>(nextManifest.layers.mainPipe.url!, controller.signal),
        ]);
        if (controller.signal.aborted) return;
        manifestRef.current = nextManifest;
        dmaDataRef.current = dma;
        loadedLayersRef.current.set("dma", dma);
        loadedLayersRef.current.set("mainPipe", mainPipe);
        setManifest(nextManifest);
        setLoading(null);
      } catch (error) {
        if (!controller.signal.aborted) setMapError(error instanceof Error ? error.message : "โหลดข้อมูลแผนที่ไม่สำเร็จ");
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!containerRef.current || mapRef.current || !manifest || !dmaDataRef.current) return;
    let disposed = false;
    void import("maplibre-gl").then((maplibregl) => {
      if (disposed || !containerRef.current) return;
      maplibregl.setWorkerUrl(maplibreWorkerUrl);
      const styleUrl = import.meta.env.VITE_MAP_STYLE_URL as string | undefined;
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: styleUrl || fallbackStyle(),
        center: [(manifest.bounds[0] + manifest.bounds[2]) / 2, (manifest.bounds[1] + manifest.bounds[3]) / 2],
        zoom: 9.5,
        minZoom: 8,
        attributionControl: false,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      map.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: "ข้อมูลโครงข่าย: Shapefile รุ่นที่ใช้งาน" }), "bottom-right");
      map.on("load", () => {
        const mainPipe = loadedLayersRef.current.get("mainPipe") ?? EMPTY;
        map.addSource(SOURCE_IDS.chaopaya, { type: "geojson", data: EMPTY, generateId: true });
        map.addLayer({ id: "chaopaya-fill", type: "fill", source: SOURCE_IDS.chaopaya, layout: { visibility: "none" }, paint: { "fill-color": "#8ecae6", "fill-opacity": 0.32 } });
        map.addLayer({ id: "chaopaya-line", type: "line", source: SOURCE_IDS.chaopaya, layout: { visibility: "none" }, paint: { "line-color": "#3d8db4", "line-width": 1.4 } });

        map.addSource(SOURCE_IDS.hydrology, { type: "geojson", data: EMPTY, generateId: true });
        map.addLayer({ id: "hydrology-line", type: "line", source: SOURCE_IDS.hydrology, minzoom: 11, layout: { visibility: "none" }, paint: { "line-color": "#5ba6c7", "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.7, 16, 2] } });

        map.addSource(SOURCE_IDS.dma, { type: "geojson", data: dmaDataRef.current!, generateId: true });
        map.addLayer({ id: "dma-fill", type: "fill", source: SOURCE_IDS.dma, layout: { visibility: "none" }, paint: { "fill-color": "#5fb7a7", "fill-opacity": 0.06 } });
        map.addLayer({ id: "dma-line", type: "line", source: SOURCE_IDS.dma, layout: { visibility: "none" }, paint: { "line-color": "#3f8e86", "line-width": 1 } });

        map.addSource(SOURCE_IDS.mainPipe, { type: "geojson", data: mainPipe, generateId: true });
        map.addLayer({ id: "main-pipe", type: "line", source: SOURCE_IDS.mainPipe, paint: { "line-color": "#0b5f68", "line-width": ["interpolate", ["linear"], ["coalesce", ["to-number", ["get", "PIPE_SIZE"]], 0], 0, 1.2, 400, 2.2, 1200, 4], "line-opacity": 0.92 } });
        map.addLayer({ id: "main-pipe-highlight", type: "line", source: SOURCE_IDS.mainPipe, filter: ["==", ["to-string", ["get", "ID"]], "__none__"], paint: { "line-color": "#ff3b30", "line-width": 6, "line-opacity": 0.9 } });

        map.addSource(SOURCE_IDS.tunnel, { type: "geojson", data: EMPTY, generateId: true });
        map.addLayer({ id: "tunnel-line", type: "line", source: SOURCE_IDS.tunnel, layout: { visibility: "none" }, paint: { "line-color": "#7957a8", "line-width": 3, "line-dasharray": [2, 1] } });

        map.addSource(SOURCE_IDS.distributionPipe, { type: "geojson", data: EMPTY, generateId: true });
        map.addLayer({ id: "distribution-pipe", type: "line", source: SOURCE_IDS.distributionPipe, minzoom: 13, layout: { visibility: "none" }, paint: {
          "line-color": ["interpolate", ["linear"], ["coalesce", ["to-number", ["get", "PIPE_SIZE"]], 0], 0, "#94a3b8", 50, "#67e8f9", 100, "#38bdf8", 150, "#22c55e", 200, "#eab308", 300, "#f97316", 400, "#dc2626"],
          "line-width": ["interpolate", ["linear"], ["coalesce", ["to-number", ["get", "PIPE_SIZE"]], 0], 0, 0.7, 100, 1, 200, 1.6, 300, 2.2, 400, 3],
          "line-opacity": 0.88,
        } });

        map.addSource(SOURCE_IDS.rtu, { type: "geojson", data: EMPTY, generateId: true });
        map.addLayer({ id: "rtu-points", type: "circle", source: SOURCE_IDS.rtu, layout: { visibility: "none" }, paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 4, 16, 8], "circle-color": ["match", ["get", "MATCH_STATUS"], "auto", "#1f9d72", "review", "#f59e0b", "#7b8794"], "circle-stroke-color": "#ffffff", "circle-stroke-width": 1.5 } });

        map.addSource(SOURCE_IDS.uz, { type: "geojson", data: EMPTY, generateId: true });
        map.addLayer({ id: "uz-points", type: "circle", source: SOURCE_IDS.uz, layout: { visibility: "none" }, paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 4, 16, 8], "circle-color": ["match", ["get", "MATCH_STATUS"], "auto", "#16a34a", "review", "#f59e0b", "#64748b"], "circle-stroke-color": "#4c1d95", "circle-stroke-width": 2 } });

        map.addSource("jobs", { type: "geojson", data: jobData, cluster: true, clusterMaxZoom: 13, clusterRadius: 42 });
        map.addLayer({ id: "clusters", type: "circle", source: "jobs", filter: ["has", "point_count"], paint: { "circle-color": "#173f49", "circle-radius": ["step", ["get", "point_count"], 15, 10, 20, 30, 26], "circle-stroke-color": "#fff", "circle-stroke-width": 2 } });
        map.addLayer({ id: "cluster-count", type: "symbol", source: "jobs", filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 13, "text-font": ["Open Sans Semibold"] }, paint: { "text-color": "#fff" } });
        map.addLayer({ id: "unclustered", type: "circle", source: "jobs", filter: ["!", ["has", "point_count"]], paint: { "circle-color": "#e07b39", "circle-radius": 5.5, "circle-stroke-color": "#fff", "circle-stroke-width": 1.5 } });

        map.addSource("selection", { type: "geojson", data: EMPTY });
        map.addLayer({ id: "selection-fill", type: "fill", source: "selection", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": "#ff3b30", "fill-opacity": 0.18 } });
        map.addLayer({ id: "selection-line", type: "line", source: "selection", filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString", "Polygon", "MultiPolygon"]]], paint: { "line-color": "#ff3b30", "line-width": 5 } });
        map.addLayer({ id: "selection-point", type: "circle", source: "selection", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-color": "#ff3b30", "circle-radius": 10, "circle-stroke-color": "#fff", "circle-stroke-width": 2 } });

        const labelDefinitions: { key: Exclude<LayerKey, "chaopaya">; source: string; placement: "line" | "point"; minzoom: number }[] = [
          { key: "dma", source: SOURCE_IDS.dma, placement: "point", minzoom: 12 },
          { key: "mainPipe", source: SOURCE_IDS.mainPipe, placement: "line", minzoom: 15 },
          { key: "distributionPipe", source: SOURCE_IDS.distributionPipe, placement: "line", minzoom: 17 },
          { key: "rtu", source: SOURCE_IDS.rtu, placement: "point", minzoom: 14 },
          { key: "uz", source: SOURCE_IDS.uz, placement: "point", minzoom: 14 },
          { key: "tunnel", source: SOURCE_IDS.tunnel, placement: "line", minzoom: 15 },
          { key: "hydrology", source: SOURCE_IDS.hydrology, placement: "line", minzoom: 15 },
        ];
        for (const definition of labelDefinitions) {
          const setting = INITIAL_SETTINGS[definition.key];
          map.addLayer({
            id: LABEL_LAYER_IDS[definition.key]!,
            type: "symbol",
            source: definition.source,
            minzoom: definition.minzoom,
            layout: {
              visibility: "none",
              "symbol-placement": definition.placement,
              "text-field": ["to-string", ["coalesce", ["get", setting.labelField], ""]],
              "text-size": definition.placement === "point" ? 13 : 12,
              "text-font": ["Open Sans Semibold"],
              "text-offset": definition.placement === "point" ? [0, 1.1] : [0, 0],
            },
            paint: { "text-color": "#163f43", "text-halo-color": "#ffffff", "text-halo-width": 1.5 },
          });
        }

        const interactive = ["dma-fill", "main-pipe", "distribution-pipe", "rtu-points", "uz-points", "tunnel-line", "hydrology-line", "chaopaya-fill", "unclustered"];
        map.on("click", (event: MapLayerMouseEvent) => {
          const activeLayers = interactive.filter((id) => map.getLayer(id) && map.getLayoutProperty(id, "visibility") !== "none");
          const features = map.queryRenderedFeatures(event.point, { layers: activeLayers });
          if (!features.length) {
            setSelections([]);
            return;
          }
          const next = features.slice(0, 12).map(asSelection);
          setSelections(next);
          setSelectionIndex(0);
          const dmaCode = next[0]?.properties.BLOCKNAME;
          if (next[0]?.layer === "dma" && dmaCode) onSelectDma(String(dmaCode));
        });
        map.on("mousemove", (event) => {
          const activeLayers = interactive.filter((id) => map.getLayer(id) && map.getLayoutProperty(id, "visibility") !== "none");
          map.getCanvas().style.cursor = map.queryRenderedFeatures(event.point, { layers: activeLayers }).length ? "pointer" : "";
        });
        map.fitBounds([[manifest.bounds[0], manifest.bounds[1]], [manifest.bounds[2], manifest.bounds[3]]], { padding: 32, duration: 0 });
        mapRef.current = map;
        setMapReady(true);
      });
    });
    return () => {
      disposed = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [manifest]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    source(map, "jobs")?.setData(jobData);
  }, [jobData, mapReady]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => mapRef.current?.resize());
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || fitRequest < 1) return;
    const jobsBounds = collectionBounds(jobData);
    if (jobsBounds) map.fitBounds(jobsBounds, { padding: 70, maxZoom: 15, duration: 650 });
    else if (manifest) map.fitBounds([[manifest.bounds[0], manifest.bounds[1]], [manifest.bounds[2], manifest.bounds[3]]], { padding: 45, duration: 650 });
  }, [fitRequest, jobData, manifest, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    for (const key of LAYER_ORDER) {
      const visible = settings[key].visible || (key === "dma" && settings.distributionPipe.visible);
      setVisibility(map, MAP_LAYER_IDS[key], visible);
      const labelId = LABEL_LAYER_IDS[key];
      if (labelId) setVisibility(map, [labelId], visible && settings[key].labels);
      if (labelId && map.getLayer(labelId)) map.setLayoutProperty(labelId, "text-field", ["to-string", ["coalesce", ["get", settings[key].labelField], ""]]);
    }
  }, [settings, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    const highlighted = [...new Set(highlightedDmaCodes.map((value) => String(value).trim()).filter(Boolean))];
    const isHighlighted: any = highlighted.length ? ["in", ["to-string", ["get", "BLOCKNAME"]], ["literal", highlighted]] : ["==", 1, 0];
    map.setPaintProperty("dma-fill", "fill-color", ["case", ["==", ["to-string", ["get", "BLOCKNAME"]], selectedDma ?? ""], "#ef8d32", isHighlighted, "#efad4a", "#5fb7a7"]);
    map.setPaintProperty("dma-fill", "fill-opacity", ["case", ["==", ["to-string", ["get", "BLOCKNAME"]], selectedDma ?? ""], 0.28, isHighlighted, 0.22, 0.035]);
    map.setPaintProperty("dma-line", "line-color", ["case", ["==", ["to-string", ["get", "BLOCKNAME"]], selectedDma ?? ""], "#c95c16", isHighlighted, "#d67b22", "#3f8e86"]);
    map.setPaintProperty("dma-line", "line-width", ["case", ["==", ["to-string", ["get", "BLOCKNAME"]], selectedDma ?? ""], 3.5, isHighlighted, 2.2, 0.8]);
    if (map.getLayer("rtu-points")) map.setFilter("rtu-points", selectedDma ? ["==", ["get", "DMA_DISPLAY"], selectedDma] : null);
    if (map.getLayer("rtu-label")) map.setFilter("rtu-label", selectedDma ? ["==", ["get", "DMA_DISPLAY"], selectedDma] : null);
    const dmaFeature = dmaDataRef.current?.features.find((item) => String(item.properties?.BLOCKNAME ?? "") === selectedDma);
    const selectedBounds = dmaFeature ? featureBounds(dmaFeature) : null;
    if (selectedBounds && settings.distributionPipe.visible) map.fitBounds(selectedBounds, { padding: 70, maxZoom: 15, duration: 650 });
    else if (highlighted.length) {
      const matches = dmaDataRef.current?.features.filter((item) => highlighted.includes(String(item.properties?.BLOCKNAME ?? ""))) ?? [];
      const bounds = matches.map(featureBounds).filter((item): item is [[number, number], [number, number]] => Boolean(item));
      if (bounds.length) map.fitBounds([
        [Math.min(...bounds.map((item) => item[0][0])), Math.min(...bounds.map((item) => item[0][1]))],
        [Math.max(...bounds.map((item) => item[1][0])), Math.max(...bounds.map((item) => item[1][1]))],
      ], { padding: 55, maxZoom: 13, duration: 650 });
    }
  }, [selectedDma, highlightedDmaCodes, mapReady, settings.distributionPipe.visible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !selected) {
      if (mapReady && map) {
        source(map, "selection")?.setData(EMPTY);
        map.setFilter("main-pipe-highlight", ["==", ["to-string", ["get", "ID"]], "__none__"]);
      }
      return;
    }
    source(map, "selection")?.setData({ type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: selected.geometry }] });
    const mainPipeId = selected.properties.MAIN_PIPE_ID;
    map.setFilter("main-pipe-highlight", ["==", ["to-string", ["get", "ID"]], mainPipeId ? String(mainPipeId) : "__none__"]);
  }, [selected, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !manifest || !settings.distributionPipe.visible || !selectedDma) {
      if (mapReady && map && !settings.distributionPipe.visible) source(map, SOURCE_IDS.distributionPipe)?.setData(EMPTY);
      return;
    }
    const controller = new AbortController();
    setLoading(`กำลังโหลดท่อจ่ายน้ำ DMA ${selectedDma}…`);
    const url = manifest.layers.distributionPipe.urlTemplate!.replace("{dma}", encodeURIComponent(selectedDma));
    void fetchJson<FeatureCollection>(url, controller.signal).then((data) => {
      if (!controller.signal.aborted) {
        loadedLayersRef.current.set("distributionPipe", data);
        source(map, SOURCE_IDS.distributionPipe)?.setData(data);
        setLoading(null);
      }
    }).catch((error) => { if (!controller.signal.aborted) { setLoading(null); setMapError(error instanceof Error ? error.message : "โหลด Distribution Pipe ไม่สำเร็จ"); } });
    return () => controller.abort();
  }, [manifest, mapReady, selectedDma, settings.distributionPipe.visible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !manifest) return;
    const controllers: AbortController[] = [];
    for (const key of ["rtu", "uz", "tunnel", "chaopaya"] as LayerKey[]) {
      if (!settings[key].visible || loadedLayersRef.current.has(key)) continue;
      const url = manifest.layers[key].url;
      if (!url) continue;
      const controller = new AbortController();
      controllers.push(controller);
      setLoading(`กำลังโหลด ${LAYER_LABELS[key]}…`);
      void fetchJson<FeatureCollection>(url, controller.signal).then((data) => {
        if (controller.signal.aborted) return;
        loadedLayersRef.current.set(key, data);
        source(map, SOURCE_IDS[key])?.setData(data);
        setLoading(null);
      }).catch((error) => { if (!controller.signal.aborted) { setLoading(null); setMapError(error instanceof Error ? error.message : `โหลด ${LAYER_LABELS[key]} ไม่สำเร็จ`); } });
    }
    return () => controllers.forEach((controller) => controller.abort());
  }, [manifest, mapReady, settings.rtu.visible, settings.uz.visible, settings.tunnel.visible, settings.chaopaya.visible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !manifest || !settings.hydrology.visible) {
      if (mapReady && map) source(map, SOURCE_IDS.hydrology)?.setData(EMPTY);
      return;
    }
    let disposed = false;
    const update = async () => {
      if (map.getZoom() < (manifest.layers.hydrology.minZoom ?? 11)) {
        source(map, SOURCE_IDS.hydrology)?.setData(EMPTY);
        setLoading(`Hydrology จะแสดงเมื่อ Zoom ≥ ${manifest.layers.hydrology.minZoom ?? 11}`);
        return;
      }
      setLoading("กำลังโหลด Hydrology ในพื้นที่ที่มองเห็น…");
      hydrologyIndexRef.current ??= await fetchJson<HydrologyIndex>(manifest.layers.hydrology.indexUrl!);
      const bounds = map.getBounds();
      const visible = hydrologyIndexRef.current.chunks.filter((chunk) => !(chunk.bounds[2] < bounds.getWest() || chunk.bounds[0] > bounds.getEast() || chunk.bounds[3] < bounds.getSouth() || chunk.bounds[1] > bounds.getNorth()));
      const collections = await Promise.all(visible.map(async (chunk) => {
        if (!hydrologyCacheRef.current.has(chunk.key)) hydrologyCacheRef.current.set(chunk.key, await fetchJson<FeatureCollection>(chunk.url));
        return hydrologyCacheRef.current.get(chunk.key)!;
      }));
      if (!disposed) {
        source(map, SOURCE_IDS.hydrology)?.setData({ type: "FeatureCollection", features: collections.flatMap((collection) => collection.features) });
        setLoading(null);
      }
    };
    const handler = () => { void update().catch((error) => { if (!disposed) { setLoading(null); setMapError(error instanceof Error ? error.message : "โหลด Hydrology ไม่สำเร็จ"); } }); };
    map.on("moveend", handler);
    handler();
    return () => { disposed = true; map.off("moveend", handler); };
  }, [manifest, mapReady, settings.hydrology.visible]);

  const updateSetting = (key: LayerKey, patch: Partial<LayerSetting>) => setSettings((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  const selectedDmaCount = manifest?.layers.distributionPipe.dmas?.find((item) => item.code === selectedDma)?.count ?? 0;
  const fields = selected ? DETAIL_FIELDS[selected.layer] ?? Object.keys(selected.properties).slice(0, 10) : [];
  const selectedJobRowId = selected?.layer === "jobs" ? String(selected.properties.rowId ?? "") : "";
  const selectedJobOriginalDma = selected?.layer === "jobs" ? String(selected.properties.originalDmaCode ?? "").trim() : "";
  const selectedJobMatchStatus = selected?.layer === "jobs" ? String(selected.properties.dmaReviewStatus ?? "") : "";
  const canCancelSelectedJobMatch = Boolean(onCancelDmaMatch && selectedJobRowId && !selectedJobOriginalDma && (selectedJobMatchStatus === "auto" || selectedJobMatchStatus === "confirmed"));
  const selectedNearestPipe = useMemo(() => {
    if (selected?.layer !== "jobs" || selected.geometry.type !== "Point") return null;
    const coordinates = selected.geometry.coordinates as [number, number];
    return nearestPipe(coordinates, [
      { layer: "Main Pipe", data: loadedLayersRef.current.get("mainPipe") },
      { layer: "Distribution Pipe", data: loadedLayersRef.current.get("distributionPipe") },
    ]);
  }, [selected, mapReady, settings.distributionPipe.visible, selectedDma]);

  const cancelSelectedJobMatch = async () => {
    if (!onCancelDmaMatch || !selectedJobRowId || !canCancelSelectedJobMatch) return;
    const jobId = String(selected?.properties.jobId ?? selectedJobRowId);
    if (!window.confirm(`ยกเลิก Match DMA ของงาน ${jobId} หรือไม่\nงานนี้ไม่มี DMA จากไฟล์ต้นฉบับ และจะกลับเป็น “ไม่ระบุ DMA”`)) return;
    setCancellingMatch(true); setMapError(null);
    try { await onCancelDmaMatch(selectedJobRowId); setSelections([]); }
    catch (error) { setMapError(error instanceof Error ? error.message : "ยกเลิก Match DMA ไม่สำเร็จ"); }
    finally { setCancellingMatch(false); }
  };

  return <div ref={shellRef} className="network-map-shell">
    <div className="map-floating-tools" aria-label="เครื่องมือแผนที่">
      <button type="button" className={layerPanelOpen ? "active" : ""} onClick={() => setLayerPanelOpen((value) => !value)} aria-expanded={layerPanelOpen} title={layerPanelOpen ? "ย่อชั้นข้อมูล" : "เปิดชั้นข้อมูล"}><span aria-hidden="true">▱</span><b>ชั้นข้อมูล</b></button>
      <button type="button" onClick={() => {
        const map = mapRef.current;
        if (!map) return;
        const jobsBounds = collectionBounds(jobData);
        if (jobsBounds) map.fitBounds(jobsBounds, { padding: 70, maxZoom: 15, duration: 650 });
        else if (manifest) map.fitBounds([[manifest.bounds[0], manifest.bounds[1]], [manifest.bounds[2], manifest.bounds[3]]], { padding: 45, duration: 650 });
      }} title="ซูมให้พอดีกับงานที่ผ่านตัวกรอง"><span aria-hidden="true">⌖</span><b>พอดีกับข้อมูล</b></button>
    </div>
    {layerPanelOpen && <aside ref={layerPanelRef} className="layer-panel" aria-label="ชั้นข้อมูลแผนที่" style={{ "--layer-panel-width": `${layerPanelWidth}px` } as CSSProperties}>
      <div className="layer-panel-title"><div><span className="eyebrow">Map layers</span><b>ชั้นข้อมูล</b></div><div><small>{manifest?.datasetVersion ?? "กำลังโหลด"}</small><button type="button" onClick={() => setLayerPanelOpen(false)} aria-label="ย่อชั้นข้อมูล">×</button></div></div>
      {LAYER_ORDER.map((key) => {
        const layer = manifest?.layers[key];
        const setting = settings[key];
        return <div className={`layer-row ${setting.visible ? "is-active" : ""}`} key={key}>
          <label className="layer-toggle"><input type="checkbox" checked={setting.visible} onChange={(event) => updateSetting(key, { visible: event.target.checked })}/><span>{LAYER_LABELS[key]}</span><em>{layer?.count?.toLocaleString("th-TH") ?? "–"}</em></label>
          {key === "distributionPipe" && setting.visible && <label className="dma-picker">เลือก DMA<select value={selectedDma ?? ""} onChange={(event) => onSelectDma(event.target.value || null)}><option value="">— เลือก DMA —</option>{layer?.dmas?.map((dma) => { const branch = branchForDma(dma.code); return <option value={dma.code} key={dma.code}>{dma.code}{branch ? ` · ${branch.shortName}` : ""} ({dma.count.toLocaleString("th-TH")})</option>; })}</select></label>}
          {layer?.labelFields?.length ? <div className="label-setting"><label><input type="checkbox" checked={setting.labels} disabled={!setting.visible && !(key === "dma" && settings.distributionPipe.visible)} onChange={(event) => updateSetting(key, { labels: event.target.checked })}/> Label</label><select aria-label={`ฟิลด์ Label ${LAYER_LABELS[key]}`} value={setting.labelField} onChange={(event) => updateSetting(key, { labelField: event.target.value })}>{layer.labelFields.map((field) => <option value={field} key={field}>{FIELD_LABELS[field] ?? field}</option>)}</select></div> : null}
        </div>;
      })}
      <div className="layer-note">Distribution Pipe โหลดเฉพาะ DMA ที่เลือก · Hydrology โหลดเฉพาะพื้นที่หน้าจอ</div>
      <div className="layer-resize-hint">ลากขอบขวาเพื่อปรับความกว้าง</div>
    </aside>}
    <div ref={containerRef} className="network-map" aria-label="แผนที่โครงข่ายประปา DMA" />
    {loading && <div className="map-status">{loading}</div>}
    {mapError && <div className="map-status map-status-error"><span>{mapError}</span><button onClick={() => setMapError(null)}>ปิด</button></div>}
    {settings.distributionPipe.visible && !selectedDma && <div className="map-hint">เลือก DMA เพื่อแสดง Distribution Pipe</div>}
    {settings.distributionPipe.visible && selectedDma && <div className="pipe-legend"><b>ขนาด Distribution Pipe — DMA {selectedDma}</b><span>{selectedDmaCount.toLocaleString("th-TH")} เส้น</span><div className="pipe-gradient"/><div><em>เล็ก</em><em>50</em><em>100</em><em>150</em><em>200</em><em>300</em><em>400 มม.</em></div></div>}
    {selected && <aside className="feature-drawer">
      <div className="feature-drawer-head"><div><span className="eyebrow">Feature details</span><b>{selected.layer === "jobs" ? "รายละเอียดงาน" : LAYER_LABELS[selected.layer as LayerKey] ?? selected.layer}</b></div><button onClick={() => setSelections([])} aria-label="ปิดรายละเอียด">×</button></div>
      {selections.length > 1 && <label className="feature-choice">พบ {selections.length} รายการ<select value={selectionIndex} onChange={(event) => setSelectionIndex(Number(event.target.value))}>{selections.map((item, index) => <option value={index} key={`${item.layer}-${index}`}>{index + 1}. {String(item.properties.NAME ?? item.properties.TAG ?? item.properties.BLOCKNAME ?? item.layer)}</option>)}</select></label>}
      <dl>{fields.map((field) => { const isJobDetail = selected.layer === "jobs" && (field === "reportedDetail" || field === "workDetail"); const value = selected.properties[field]; return <div key={field} className={isJobDetail ? "job-detail-row" : undefined}><dt>{FIELD_LABELS[field] ?? field}</dt><dd>{value === null || value === undefined || value === "" ? isJobDetail ? "ไม่มีข้อมูล" : "—" : String(value)}</dd></div>; })}</dl>
      {selected.layer === "jobs" && selectedNearestPipe && <div className="drawer-nearest-pipe"><b>ท่อจาก Shape ที่ใกล้จุดงานที่สุด</b><span>{selectedNearestPipe.layer} · รหัส {String(selectedNearestPipe.properties.ID ?? selectedNearestPipe.properties.FID_Distri ?? "ไม่ระบุ")}</span><span>ขนาด {String(selectedNearestPipe.properties.PIPE_SIZE ?? "ไม่ระบุ")} มม. · ระยะประมาณ {Math.round(selectedNearestPipe.distance).toLocaleString("th-TH")} ม.</span><small>คำนวณจากระยะเชิงพื้นที่ ไม่ใช่การยืนยันว่าท่อนี้เกี่ยวข้องหรือเสียหาย</small></div>}
      {selected.layer === "jobs" && selectedJobOriginalDma && <div className="drawer-match-source"><b>DMA จากไฟล์ต้นฉบับ</b><span>ไม่สามารถยกเลิก Match จากแผนที่ได้</span></div>}
      {canCancelSelectedJobMatch && <div className="drawer-match-actions"><div><b>DMA จากการ Match ในระบบ</b><span>หากตำแหน่งหรือ DMA ไม่ถูกต้อง สามารถยกเลิกและส่งกลับไปตรวจสอบได้</span></div><button type="button" disabled={cancellingMatch} onClick={() => void cancelSelectedJobMatch()}>{cancellingMatch ? "กำลังยกเลิก…" : "ยกเลิก Match DMA"}</button></div>}
      {selected.properties.MATCH_STATUS === "review" && <div className="drawer-warning">ความสัมพันธ์กับ Main Pipe ต้องตรวจสอบเพิ่มเติม</div>}
      {selected.properties.DMA_MATCH_STATUS === "source-spatial-different" && <div className="drawer-warning">รหัส DMA เชิงปฏิบัติการต่างจาก Polygon ที่จุดตั้งอยู่ โดยระบบยังคงใช้รหัสต้นฉบับ</div>}
    </aside>}
  </div>;
}
